import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { toLinkedInEmbedUrl } from './linkedin-embed';

interface LinkPreviewModalProps {
    title: string;
    url: string;
    onClose: () => void;
}

const LOADING_TIMEOUT_MS = 8000;

const isSameOrigin = (url: string): boolean => {
    try {
        return new URL(url).origin === window.location.origin;
    } catch {
        return false;
    }
};

const getHostname = (url: string): string => {
    try {
        return new URL(url).hostname;
    } catch {
        return url;
    }
};

// Hosts conhecidos por permitir ser exibidos em iframe de terceiros
// (não enviam X-Frame-Options/CSP frame-ancestors bloqueando). Curada
// manualmente por enquanto — ver conversa sobre checagem dinâmica de header.
const EMBEDDABLE_HOST_SUFFIXES = ['.github.io'];

const isKnownEmbeddableHost = (url: string): boolean => {
    const hostname = getHostname(url);
    return EMBEDDABLE_HOST_SUFFIXES.some(suffix => hostname.endsWith(suffix));
};

const LinkPreviewModal = ({ title, url, onClose }: LinkPreviewModalProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const linkedInEmbedUrl = useMemo(() => toLinkedInEmbedUrl(url), [url]);
    const canEmbed = useMemo(
        () => isSameOrigin(url) || linkedInEmbedUrl !== null || isKnownEmbeddableHost(url),
        [url, linkedInEmbedUrl]
    );
    const iframeSrc = linkedInEmbedUrl ?? url;
    const hostname = useMemo(() => getHostname(url), [url]);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [onClose]);

    useEffect(() => {
        if (!canEmbed) return;
        const timeoutId = window.setTimeout(() => setIsLoading(false), LOADING_TIMEOUT_MS);
        return () => window.clearTimeout(timeoutId);
    }, [url, canEmbed]);

    const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return ReactDOM.createPortal(
        <div className="lp-overlay" onClick={onOverlayClick}>
            <div className="lp-panel" role="dialog" aria-modal="true" aria-label={`Preview: ${title}`}>
                <div className="lp-titlebar">
                    <span className="lp-title-prompt">
                        <span className="prompt-dollar">$</span>
                        <span className="prompt-cmd">curl</span>
                        <span className="lp-title-url">{url}</span>
                    </span>
                    <div className="lp-titlebar-actions">
                        <a className="lp-newtab" href={url} target="_blank" rel="noopener noreferrer">
                            Abrir em nova aba ↗
                        </a>
                        <button type="button" className="lp-close" onClick={onClose} aria-label="Fechar preview">
                            [x]
                        </button>
                    </div>
                </div>
                <div className="lp-body">
                    {canEmbed ? (
                        <>
                            {isLoading && (
                                <div className="lp-loading" aria-live="polite">
                                    <span className="lp-spinner" aria-hidden="true"></span>
                                    <span className="lp-loading-text">carregando {url} ...</span>
                                </div>
                            )}
                            <iframe
                                className="lp-iframe"
                                src={iframeSrc}
                                title={title}
                                onLoad={() => setIsLoading(false)}
                                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                                referrerPolicy="no-referrer"
                            />
                        </>
                    ) : (
                        <div className="lp-external-notice">
                            <p className="lp-external-notice-text">
                                [!] <strong>{hostname}</strong> não permite visualização embutida.
                                A postagem só pode ser vista diretamente na origem.
                            </p>
                            <a className="lp-external-notice-btn" href={url} target="_blank" rel="noopener noreferrer">
                                Abrir em nova aba ↗
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default LinkPreviewModal;
